<script>
   /*************************************************************/
   /* Root component for any application                        */
   /* ----------------------------------                        */
   /* universal wrapper for any application                     */
   /* implements routing among tabs/containers                  */
   /* and session management                                    */
   /*************************************************************/

   import { onMount, tick } from 'svelte';
   import { date2str, splitSession, refsOf, joinSession, arrayKey, SPLIT_MIN, dropResiduals,
      sessionFileParts, decodeSessionFile, isTooLarge, sessionFileMessage, sessionFileError,
      couldBeJson, resultsFit, modelsFit,
      reviver as defaultReviver, replacer as defaultReplacer } from '../utils';
   import Modal from './Modal.svelte';
   import { withErrorLines } from '@mdatools-apps/wasm/utils.js';

   let {
      WASM,                   // WASM promise from main app
      appID,                  // application ID (as string) is used for saving sessions to local storage
      appTitle,               // application title (for header)
      appShortTitle,          // application short title (for header)
      appSlogan,              // application slogan (for header)
      tabs = [],              // array with tab properties (name, label, component)
      initSession,            // callback which initialize JSON with app session
      checkSession,           // callback which checks validity of the JSON with app session loaded by user
      info,                   // snippet with HTMl for information window
      footer,                 // snippet with HTML for footer
      replacer = defaultReplacer,
      reviver = defaultReviver,
   } = $props();

   // the C code prints why it failed to stderr and returns a code; a module built through this
   // keeps the lines, and the wrappers throw them instead of 'unexpected error' ('wasmError')
   const wasm = $derived(withErrorLines(WASM));

   const dbName = $derived('mdatools-' + appID);       // database name (appID will be used for storage name)

   let isInfoShown = $state(false);                   // show or not info modal
   let session = $state();                            // current session
   const SESSION_MESSAGE = 'Current session ';        // message the session corner shows by default
   let sessionMessage = $state(SESSION_MESSAGE);      // message to show in session corner
   let sessionHideButtons = $state(false);            // hide or not session buttons
   let preparing = $state(false);                     // a session file is being written
   let loadingFile = $state(false);                   // a session file is being read
   let sessionError = $state('');                     // error to show as a floating note
   let errorTimer;                                    // timer which hides the note again
   let backupProducer = null;                         // returns the session of the pending backup
   let backupRetry = null;                            // a retry's wait size, else null
   let tabProducer = null;                            // last producer a tab passed, for retries
   let backupTimer = null;                            // timer which will write the pending backup
   const BACKUP_DELAY = 500;                          // ms to wait for a burst of backups to end
   const OPEN_DELAY = 2000;                           // ms the open at mount may take before the
                                                      // session starts in memory
   let backupRequested = false;                       // a backup was asked for since the mount
   let db = null;                                     // connection opened at mount, kept for the writes
   let notSaved = $state(false);                      // the last backup failed: the stored session is older
   let writeRev = 0;                                  // number of the latest write, failure, skipped
                                                      // write or reset; a write clears 'notSaved'
                                                      // only if it is the latest
   const stored = new Set();                          // keys of array records known on disk
   let storeEpoch = 0;                                // bumped when a restore's keys go stale
   let skip = null;                                   // {n}: skip saves of >= n new bytes
   let skipTimer = null;                              // timer which ends the skip
   const QUOTA_WAIT = typeof __QUOTA_WAIT__ === 'number' ? __QUOTA_WAIT__ : 60000;
   const TOO_LARGE = 'Session is too large to be saved in this browser. ' +
      'Changes since the last save are lost on reload.';
   const SAVE_FAILED = 'Session could not be saved.';

   /* tells if an error is of the kind an engine throws for a string longer than it allows: a
      RangeError in V8 and JavaScriptCore, an InternalError in SpiderMonkey */
   const isTooLong = (e) => e instanceof RangeError || e?.name === 'InternalError';

   /* runs fn once the browser has drawn the current state: writing or parsing a large session
      blocks the page for seconds, so the text saying so must be on the screen first */
   const afterPaint = (fn) => requestAnimationFrame(() => setTimeout(fn));

   /* shows session message */
   function showMessage(msg, delay = 1000) {
      sessionMessage = msg;
      sessionHideButtons = true;
      setTimeout(() => {
         sessionMessage = SESSION_MESSAGE;
         sessionHideButtons = false;
      }, delay);
   }

   /* shows a session error as a floating note in the corner of the app. errors are not shown in
      the line the ordinary messages use: that line sits between the session buttons and hides
      them while it is there, so a message about something going wrong took away the very buttons
      the user needs to load or reset the session */
   function showError(msg, delay = 6000) {
      clearTimeout(errorTimer);
      sessionError = msg;
      errorTimer = setTimeout(() => sessionError = '', delay);
   }

   /* hides the error note before it times out */
   function hideError() {
      clearTimeout(errorTimer);
      sessionError = '';
   }


   /* callback to open modal with app info */
   function showInfo() {
      isInfoShown = true;
   }

   /* callback to close modal with app info */
   function closeInfo() {
      isInfoShown = false;
   }

   /* callback to select tab */
   function selectTab(id) {
      session.tab = id;

      // the selected tab is part of the session, but nothing else would ever save it. the backup
      // effects live in the tab components and each is triggered by the values it passes to
      // 'backupSession' after the function which returns the session - those extra arguments
      // look unused, and they are, but reading them is what registers them as dependencies of the
      // effect. none of them is the tab index, so switching tab and reloading used to bring back
      // the tab left before
      queueBackup(() => session);
   }

   /* callback to reset the session in database and in the app window */
   function resetSessionStorage() {
      // a backup still waiting for its delay would be written after the delete below and bring
      // the session the user asked to destroy straight back on the next reload
      cancelBackup();

      // the session in the window is only replaced once the stored one is really gone. clearing
      // it first would show an empty app while the old session was still on disk, and the next
      // reload would bring it back
      openDB().then((conn) => {
         const tx = conn.transaction(appID, "readwrite");
         const store = tx.objectStore(appID);
         // once more, for a backup queued while the open above was pending
         cancelBackup();
         // the array records go too, so nothing is left for a later save to find
         store.clear();
         // a write issued earlier on the kept connection ends before this transaction; a failure it
         // reports from now on is ignored
         writeRev++;

         tx.oncomplete = () => {
            notSaved = false;
            forgetStore();
            session = initSession();
            showMessage('Session was reinitialized.');
         };

         // a failed request and a transaction ended without a commit both fire 'abort'. the session
         // in the window stays, and it is saved again: the bump above ignored a write which may
         // have failed too
         tx.onabort = () => {
            console.error('Failed to delete session from DB.', tx.error);
            showError('Session could not be reset.');
            queueBackup(() => session);
         };
      }).catch((e) => {
         console.error('Failed to reset the session in the database.', e);
         showError('Session could not be reset.');
         // the session in the window stays, and a backup the cancel above dropped is made again
         queueBackup(() => session);
      });
   }

   /* opens the database and returns the connection in a promise. a database without the store
      is opened again one version up, once, which creates it. an open which waits for another
      window of the app to close the database is given up */
   function openDB(version, repaired = false) {
      return new Promise((resolve, reject) => {
         const request = indexedDB.open(dbName, version);
         let blocked = false;
         request.onupgradeneeded = () => {
            const conn = request.result;
            if (!conn.objectStoreNames.contains(appID)) conn.createObjectStore(appID);
         };
         request.onblocked = () => {
            blocked = true;
            reject(new Error('The database is open in another window.'));
         };
         request.onerror = () => reject(request.error);
         request.onsuccess = () => {
            const conn = request.result;
            if (blocked) {
               conn.close();
            } else if (conn.objectStoreNames.contains(appID)) {
               resolve(conn);
            } else if (repaired) {
               conn.close();
               reject(new Error('The database has no store for the session.'));
            } else {
               const next = conn.version + 1;
               conn.close();
               resolve(openDB(next, true));
            }
         };
      });
   }

   /* keeps a connection for the writes. it is closed when another window of the app raises the
      version, which waits for every open connection; the next write opens a new one */
   function keepConnection(conn) {
      conn.onversionchange = () => {
         conn.close();
         if (db === conn) db = null;
      };
      db = conn;
   }

   /**
    * Saves the current session to the database.
    *
    * @param producer - a function that returns the session. it is called when the write is due and
    * not before, so a burst of calls - the tab components all mounting at once, or a slider
    * writing a session field on every step - costs one write, not one per call. it runs from a
    * timer or from the handler of the page going hidden, outside any effect, so reading the session
    * in it registers nothing. it may return null to withdraw the write, when the state it would
    * save is mid-transition and the change which ends the transition queues a new one. a string is
    * refused: the session is split into records here, and a string would hide every array in it.
    * the last producer is kept: a retry after a full disk runs it, so a tab's guard still holds.
    * @param deps - deliberately unused HERE. each tab's backup effect lists, after the producer,
    * the session fields it must save on, and READING them at the call site is what registers the
    * effect's dependencies - they have finished their whole job before this function is entered.
    * they are declared so the signature does not read as a leftover from a wider one and invite
    * someone to trim the CALL SITES, which is what would silently stop those tabs saving on those
    * fields, with nothing in the build, the tests or the app going red. deleting this parameter on
    * its own changes nothing: a call site evaluates its arguments either way.
    *
    * three rules govern what a list must contain, none of them enforced anywhere - a field
    * written in the same flush as a listed one rides along, but one written after an await needs
    * its own entry; a field mutated in place inside a listed object needs its own entry, because
    * reading a parent does not subscribe to its children; and an array needs '.length', because
    * reading the property registers its identity and not its contents.
    */
   function backupSession(producer, ...deps) {
      tabProducer = producer;
      queueBackup(producer);
   }

   /* queues a backup made by 'producer'. 'retry' is the size of the wait a retry follows, which a
      failed retry starts again unless it failed for want of space; null for any other backup */
   function queueBackup(producer, retry = null) {
      backupRequested = true;
      backupProducer = producer;
      backupRetry = retry;
      clearTimeout(backupTimer);
      backupTimer = setTimeout(flushBackup, BACKUP_DELAY);
   }

   /* queues a retry of the last producer a tab passed, unless a backup is pending already */
   function queueRetry(retry) {
      if (backupTimer || !tabProducer) return;
      queueBackup(tabProducer, retry);
   }

   /* drops the pending backup, if there is one */
   function cancelBackup() {
      clearTimeout(backupTimer);
      backupTimer = null;
      backupProducer = null;
   }

   /* writes the pending backup now, if there is one */
   function flushBackup() {
      const producer = backupProducer;
      const retry = backupRetry;
      cancelBackup();
      if (!producer) return;

      let value;
      try {
         value = producer();
         if (typeof value === 'string') throw new TypeError('A backup producer returned a string.');
      } catch (e) {
         console.error('Failed to get the session for a backup.', e);
         backupFailed(SAVE_FAILED);
         return;
      }
      if (value != null) writeSession(value, retry);
   }

   /* reports a failed backup, with a note unless 'quiet', and marks the stored session as older
      than the one in the window */
   function backupFailed(msg, quiet = false) {
      writeRev++;
      notSaved = true;
      if (!quiet) showError(msg, 10000);
      transact((tx) => {
         tx.objectStore(appID).put(true, "unsaved");
         tx.onabort = () => console.error('Failed to mark the stored session as older.', tx.error);
      }, () => {});
   }

   /* skips the saves with at least 'n' bytes of new arrays for QUOTA_WAIT ms, after a save of that
      many was refused for want of space or a retry failed; then a retry is queued if the session is
      not saved */
   function startSkip(n) {
      clearTimeout(skipTimer);
      skip = {n};
      skipTimer = setTimeout(() => {
         skip = null;
         skipTimer = null;
         if (notSaved) queueRetry(n);
      }, QUOTA_WAIT);
   }

   /* ends the skip and its timer */
   function endSkip() {
      clearTimeout(skipTimer);
      skipTimer = null;
      skip = null;
   }

   /* forgets what is known of the store, after a reset or a file load (a new connection empties
      only 'stored'). a backup queued meanwhile, a retry say, would save the session replaced */
   function forgetStore() {
      cancelBackup();
      stored.clear();
      storeEpoch++;
      endSkip();
      tabProducer = null;
   }

   /* gives 'fill' a write transaction on the connection opened at mount, or on a fresh one, and
      calls 'failed' if none can be had. a kept connection which throws - closed by the browser, say
      when site data was cleared - is dropped for the fresh one. a throw of 'fill' is not caught on
      the kept connection; on a fresh one it is reported as a failed open */
   function transact(fill, failed) {
      const run = (conn) => {
         let tx;
         try {
            tx = conn.transaction(appID, "readwrite");
         } catch (e) {
            console.error('Failed to write to the database.', e);
            return false;
         }
         fill(tx);
         return true;
      };

      if (db) {
         if (run(db)) return;
         db = null;
      }

      openDB().then((conn) => {
         keepConnection(conn);
         stored.clear();
         storeEpoch++;
         if (!run(conn)) failed();
      }).catch((e) => {
         console.error('Failed to open the database.', e);
         failed();
      });
   }

   /* saves the session as a skeleton and a record per large array (see 'splitSession'). the
      connection opened at mount is used when it is there, so that a flush from the page going
      hidden issues its first requests in the same task: an open of its own is answered later,
      which a page being closed may never run. even so the write is best effort - the browser can
      drop a transaction it has not committed when the page is torn down */
   function writeSession(value, retry) {
      // a failed backup must leave the session in the window alone. losing the copy on disk is
      // recoverable, the work the user has open is not
      const rev = ++writeRev;
      storeEpoch++;
      transact((tx) => {
         // why this save aborted, if it aborted itself: 'tx.error' is null after 'tx.abort()'
         let failure = null, skipped = false, mismatch = false;
         let fresh = 0;
         let arrays = new Map();
         const abort = () => { try { tx.abort(); } catch (e) {} };

         // a failure is marked quietly in a retry, and a retry which fails waits again
         const fail = (msg, n = retry) => {
            if (rev !== writeRev) return;
            backupFailed(msg, retry !== null);
            if (n !== null) startSkip(n);
         };

         // an overtaken save leaves the skip: a later save may be waiting on it
         tx.oncomplete = () => {
            if (rev !== writeRev) return;
            endSkip();
            notSaved = false;
            stored.clear();
            for (const key of arrays.keys()) stored.add(key);
         };

         // a failed request and a transaction ended without a commit both fire 'abort'. an
         // overtaken save's failure is ignored: the later save or failure has the final word
         tx.onabort = () => {
            // an array changed in place while it was saved: the next save hashes it again
            if (mismatch) return queueRetry(retry);
            if (rev !== writeRev) return;
            if (failure) return fail(failure);
            if (skipped) return backupFailed(SAVE_FAILED, true);
            console.error('Failed to write the session to the database.', tx.error);
            const full = tx.error?.name === 'QuotaExceededError';
            if (full && fresh > 0) return fail(SAVE_FAILED, fresh);
            fail(SAVE_FAILED);
         };

         let skeleton;
         try {
            ({skeleton, arrays} = splitSession(value, replacer));
         } catch (e) {
            console.error('Failed to serialise the session.', e);
            failure = isTooLong(e) ? TOO_LARGE : SAVE_FAILED;
            return abort();
         }

         // a save as large as one the disk had no room for is skipped while the wait lasts
         for (const [key, a] of arrays) if (!stored.has(key)) fresh += a.values.byteLength;
         if (skip && fresh >= skip.n) {
            skipped = true;
            return abort();
         }

         // the records this save does not refer to are deleted below
         for (const key of stored) if (!arrays.has(key)) stored.delete(key);

         try {
            // one transaction: the records, the skeleton and the removal of the mark commit
            // together or not at all
            const store = tx.objectStore(appID);
            for (const [key, a] of arrays) if (!stored.has(key)) store.put(a.values, key);
            store.put(skeleton, "session");
            store.delete("unsaved");

            // another window of the app may have deleted a record this one thinks is on disk, and
            // may have left records nothing refers to
            const keys = store.getAllKeys(IDBKeyRange.bound('a:', 'a:\uffff'));
            keys.onsuccess = () => {
               try {
                  const onDisk = new Set(keys.result);
                  for (const [key, a] of arrays) {
                     if (onDisk.has(key)) continue;
                     if (arrayKey(a.type, a.values) !== key) {
                        mismatch = true;
                        return abort();
                     }
                     store.put(a.values, key);
                  }
                  for (const key of onDisk) if (!arrays.has(key)) store.delete(key);
                  tx.commit?.();
               } catch (e) {
                  console.error('Failed to write the session to the database.', e);
                  failure = SAVE_FAILED;
                  abort();
               }
            };
         } catch (e) {
            console.error('Failed to write the session to the database.', e);
            failure = SAVE_FAILED;
            abort();
         }
      }, () => {
         if (rev === writeRev) {
            backupFailed(SAVE_FAILED, retry !== null);
            if (retry !== null) startSkip(retry);
         }
      });
   }

   /* reads the last session over the connection opened at mount. a session already on screen
      is replaced only when no backup was asked for by the time the read answers */
   function restoreSession(conn) {
      const superseded = () => session !== undefined && backupRequested;
      const epoch = storeEpoch;
      const rev = writeRev;

      new Promise((resolve, reject) => {
         // 'transaction' throws at once when the store is gone; a throw here rejects the promise
         const tx = conn.transaction(appID, "readonly");
         const store = tx.objectStore(appID);
         let records = new Map();
         const getRequest = store.get("session");
         const markRequest = store.get("unsaved");
         // the arrays the skeleton refers to are read in the same transaction. a skeleton which
         // does not parse is left for 'parseSession' to refuse, so only a failed read aborts
         getRequest.onsuccess = () => {
            // text with no reference, such as a session an earlier build stored as one string, is
            // parsed once, as that build did: a parse to look for references is wasted on it
            const str = getRequest.result;
            if (typeof str !== 'string' || !str.includes('"ref":"a:')) {
               records = null;
               return;
            }
            let refs;
            try {
               refs = refsOf(getRequest.result);
            } catch (e) {
               return;
            }
            for (const key of refs) {
               const request = store.get(key);
               request.onsuccess = () => {
                  if (request.result !== undefined) records.set(key, request.result);
               };
            }
         };
         // a failed request aborts the transaction, and an abort settles the promise too
         tx.oncomplete = () => resolve([getRequest.result, markRequest.result === true, records]);
         tx.onabort = () => reject(tx.error ?? new Error('The session could not be read.'));
      })
      .then(([data, old, records]) => {
            if (superseded()) return;
            const parsed = parseSession(data, records);

            if (parsed) {
               session = parsed.session;
               // the records read are on disk, unless a save or a reset came meanwhile
               if (records && storeEpoch === epoch) records.forEach((_, key) => stored.add(key));
               // saved again: a session whose stored results held E, and text with no reference
               // which a save would now split, an earlier build's one string say. on the
               // connection kept at mount it is written before the app is drawn: a backup queued
               // for later would skip a tab's guard. on a fresh connection it is split after the
               // draw. not when a save, a failure, a reset or a file load came since the read,
               // which it would undo
               if (!old && writeRev === rev &&
                  (parsed.cut || (!records && splits(data, parsed.session)))) {
                  writeSession(session, null);
               }
               if (!old) {
                  showMessage('Last session is restored ', 1500);
                  return;
               }
               // the mark is dropped, so this is told once: the session in the window is now the
               // stored one
               showError('Last saved session is restored; later changes could not be saved.', 10000);
               transact((tx) => tx.objectStore(appID).delete("unsaved"), () => {});
               return;
            }

            // nothing stored yet is a first visit and needs no message, unless the mark says a save
            // failed. something stored which can not be read is not: announcing a restore over an
            // empty app told the user their work was there when it was gone
            session = initSession();
            if (data) {
               showError('Last session could not be restored.');
               // a tab need not back up the session it mounts with - the pls tabs do not - so the
               // record which could not be read is replaced here, or the same error would greet
               // every reload
               queueBackup(() => session);
            } else if (old) {
               showError('Last session could not be saved, so it is not restored.', 10000);
               transact((tx) => tx.objectStore(appID).delete("unsaved"), () => {});
            }
      })
      .catch((err) => {
         // the record is left until the next backup
         console.error(err);
         if (superseded()) return;
         session = initSession();
         showError('Last session could not be restored.');
      });
   }

   /* whether saving 'value', read from 'text', writes array records. text shorter than
      2 * SPLIT_MIN holds no array of SPLIT_MIN values; a value which can not be split is a no */
   function splits(text, value) {
      if (text.length < 2 * SPLIT_MIN) return false;
      try {
         return splitSession(value, replacer).arrays.size > 0;
      } catch (e) {
         return false;
      }
   }

   /* parses a session string and returns {session, cut}, or null if it can not be used; 'cut' is
      true if E was dropped from its results. it assigns nothing itself: a session restored from
      the database and one picked from a file have to be handled differently, and only the caller
      knows which of the two it is dealing with.
      'records' holds the arrays a skeleton refers to; an older JSON file has none. with
      'tellTooLarge', a session too large for the browser to parse, join or cut residuals from
      throws an error of kind 'tooLarge' */
   function parseSession(str, records, tellTooLarge = false) {

      if (!str || str === 'undefined') return null;

      let _session;
      try {
         _session = records ? joinSession(str, records, reviver) : JSON.parse(str, reviver);
      } catch (e) {
         // text which is not json at all, a missing or damaged record, or a value the app's
         // reviver refuses
         if (tellTooLarge && isTooLarge(e)) throw sessionFileError('tooLarge', e);
         console.error(e);
         return null;
      }

      try {
         // a result or model whose values do not fit its sizes is still read by offset
         if (!checkSession(_session) || !resultsFit(_session) || !modelsFit(_session)) return null;
         // the tab index selects the tab shown and no app checks it
         if (!Number.isInteger(_session.tab) || _session.tab < 0 || _session.tab >= tabs.length) {
            _session.tab = 0;
         }
      } catch (e) {
         // 'checkSession' throws on an object of the wrong shape, since it reads into the session
         // before it knows what is there; a RangeError there can be a damaged length
         console.error(e);
         return null;
      }

      try {
         return {session: _session, cut: dropResiduals(_session)};
      } catch (e) {
         // the session is checked, so this is a copy the browser has no room for
         if (tellTooLarge && isTooLarge(e)) throw sessionFileError('tooLarge', e);
         console.error(e);
         return null;
      }
   }

   /* callback to download the session as a file */
   function downloadSession(e) {
      if (preparing) return;
      // the buttons are hidden meanwhile, and a hidden button loses the focus a keyboard gave it
      const button = document.activeElement === e.currentTarget ? e.currentTarget : null;
      preparing = true;
      afterPaint(() => {
         try {
            writeSessionFile();
         } finally {
            preparing = false;
            if (button) tick().then(() => button.focus());
         }
      });
   }

   /* writes the session to a binary session file and starts its download */
   function writeSessionFile() {

      const dateStr = date2str(new Date());
      const fileName = appID + '-session-' + dateStr + '.session';

      try {
         const { skeleton, arrays } = splitSession({...session, date: dateStr}, replacer);
         const blob = new Blob(sessionFileParts(skeleton, arrays, appID),
            { type: 'application/octet-stream' });
         const url = window.URL.createObjectURL(blob);
         // the browser may read the file after the click has returned, or the click may throw
         setTimeout(() => URL.revokeObjectURL(url), 60000);
         const a = document.createElement('a');
         a.setAttribute('href', url);
         a.setAttribute('download', fileName);
         a.click();
      } catch (e) {
         console.error(e);
         showError(isTooLarge(e) ? 'Session is too large to be downloaded in this browser.' :
            'Session could not be written to a file.');
      }
   }

   /* callback to load a session from a file: a session file, or an older JSON one */
   function loadSession (e) {

      const input = e.target;
      const file = input.files[0];
      if (!file) return;

      // the input is cleared on every path and not only after a successful load. it holds the name
      // of the file which was picked, and while it is still there choosing the same file again
      // fires no event at all, so after one failure the control did nothing until the page was
      // reloaded
      loadingFile = true;
      afterPaint(async () => {
         try {
            const decoded = await decodeSessionFile((a, b) => file.slice(a, b).arrayBuffer(),
               file.size);
            if (decoded) {
               readSession(parseSession(decoded.skeleton, decoded.records, true));
            } else {
               await readJsonFile(file);
            }
         } catch (e) {
            console.error(e);
            showError(sessionFileMessage(e));
         } finally {
            loadingFile = false;
            input.value = '';
         }
      });
   }

   /* reads an older session file, written as JSON; settles once the file has been handled */
   async function readJsonFile(file) {
      // a file whose first bytes rule JSON out is not read whole
      if (!couldBeJson(new Uint8Array(await file.slice(0, 1024).arrayBuffer()))) {
         throw sessionFileError('refused');
      }
      await new Promise((resolve, reject) => {
         const reader = new FileReader();
         reader.onload = () => afterPaint(() => {
            try {
               // the file is not empty, so no text means more than the browser can hold
               if (reader.result === '') throw sessionFileError('tooLarge');
               readSession(parseSession(reader.result, undefined, true));
               resolve();
            } catch (e) {
               reject(e);
            }
         });
         reader.onerror = () => reject(reader.error);
         reader.readAsText(file);
      });
   }

   /* puts a session read from a file in place of the current one */
   function readSession(loaded) {
      if (loaded) {
         session = loaded.session;
         // a save issued before now reports nothing: a failure of it would skip the loaded
         // session's save, and no retry would follow the wait
         writeRev++;
         forgetStore();
         showMessage('Session is loaded ');
         // a tab need not back up the session it mounts with - the pls tabs do not - so a session
         // read from a file is written here
         queueBackup(() => session);
      } else {
         // the current session stays. the file the user picked is not a session for this app,
         // and throwing away what they had open because of it is the worse of the two outcomes
         showError('The file does not contain a session for this app.');
      }
   }

   function confirmAndReset() {
      if (confirm("Are you sure? All current results and models will be lost in all tabs.")) {
         resetSessionStorage();
      }
   }

   onMount(() => {
      // the open waits, with no event, while a window of an earlier build blocks a version raise
      // of another window. after OPEN_DELAY the session starts in memory; a connection arriving
      // later is kept for the writes and the record is restored unless a backup was asked for
      const timer = setTimeout(() => {
         session = initSession();
      }, OPEN_DELAY);

      openDB().then((conn) => {
         clearTimeout(timer);
         keepConnection(conn);
         restoreSession(conn);
      }).catch((e) => {
         // without this the app renders nothing at all when the database can not be opened - in a
         // private window, with storage blocked, or after an interrupted upgrade. 'session' would
         // stay undefined and the whole interface is behind a check on it, so there was not even a
         // way to reset and carry on. the session is only kept in memory then, but the app works
         clearTimeout(timer);
         console.error('Failed to open the database.', e);
         if (session === undefined) session = initSession();
      });

      // a pending backup is written as soon as the page goes hidden - a closed tab, a switch to
      // another one, an app sent to the background - rather than when its delay runs out, which
      // a page on its way out may not live to see
      const flushWhenHidden = () => {
         if (document.visibilityState === 'hidden') flushBackup();
      };
      document.addEventListener('visibilitychange', flushWhenHidden);
      return () => document.removeEventListener('visibilitychange', flushWhenHidden);
   });
</script>

{#if session}
<!-- hidden, not unmounted, while a file is read: a file which is not a session leaves it as it was -->
<div class="page" class:loading-file={loadingFile}>

   <header class="page-header">
      <div class="header-group">
         <img class="logo" alt={appShortTitle} src="logo.svg" />
         <h1>{appTitle}</h1>
         <p>{appSlogan}</p>
      </div>
      <ul class="nav">
         {#each tabs as tab, i}
         <li class="nav-item" class:selected={session.tab === i} >
            <!-- tabindex="0" on this and the buttons below: Safari's default Tab skips a button
                 without one -->
            <button onclick={() => selectTab(i)} tabindex="0">{tab.name}</button>
         </li>
         {/each}
      </ul>
   </header>

   <main>
      {#key session.date}
      {#each tabs as tab, i}
      <div class:hidden={session.tab !== i} class="tab-content tab-content-{tab.label}">
      <tab.component WASM={wasm} {session} {backupSession}/>
      </div>
      {/each}
      {/key}
   </main>

   {#if sessionError}
   <!-- 'role=alert' makes this an assertive live region, so a screen reader announces the note
        when it appears without the user having to go looking for it -->
   <div class="session-error" role="alert">
      <span>{sessionError}</span>
      <button class="session-error-close" onclick={hideError} aria-label="close message"
         tabindex="0">
         <svg xmlns="http://www.w3.org/2000/svg" version="2.0" width="14px" height="14px" viewBox="0 0 100 100">
         <line x1="25" y1="25" x2="75" y2="75"/>
         <line x1="25" y1="75" x2="75" y2="25"/>
         </svg>
      </button>
   </div>
   {/if}

   <footer>
      <div class="session" class:no-buttons={sessionHideButtons || preparing}>
         <span class="session-message">{preparing ? 'Preparing session file... ' :
            notSaved && sessionMessage === SESSION_MESSAGE ? 'Session is not saved ' :
            sessionMessage}</span>
         <label title="load session from file" class="upload-button"><input type="file" onchange={loadSession}/>
            <svg xmlns="http://www.w3.org/2000/svg" version="2.0" width="20px" height="20px" viewBox="0 0 100 100">
            <line x1="50" y1="25" x2="50" y2="75"/>
            <line x1="30" y1="40" x2="50" y2="25"/>
            <line x1="70" y1="40" x2="50" y2="25"/>
            </svg>
         </label>
         <button title="download session as a file" aria-label="download session as a file"
            class="download-button" onclick={downloadSession} tabindex="0">
            <svg xmlns="http://www.w3.org/2000/svg" version="2.0" width="20px" height="20px" viewBox="0 0 100 100">
            <line x1="50" y1="25" x2="50" y2="75"/>
            <line x1="30" y1="60" x2="50" y2="75"/>
            <line x1="70" y1="60" x2="50" y2="75"/>
            </svg>
         </button>
         <button title="reset all sessions" aria-label="reset all sessions" class="clear-button"
            onclick={confirmAndReset} tabindex="0">
            <svg xmlns="http://www.w3.org/2000/svg" version="2.0" width="20px" height="20px" viewBox="0 0 100 100">
            <line x1="25" y1="25" x2="75" y2="75"/>
            <line x1="25" y1="75" x2="75" y2="25"/>
            </svg>
         </button>
      </div>
      <div class="info">
      <button title="Show information about the app" class="button-info" onclick={showInfo}
         tabindex="0">info</button>
      {@render footer()}
      </div>
   </footer>

   {#if isInfoShown}
   <Modal closeModal={closeInfo}>
      <div class="info-container">
      {@render info()}
      </div>
   </Modal>
   {/if}
</div>
{/if}
{#if !session || loadingFile}
<!-- the app renders nothing until the session is in place, and restoring or reading a large one
     can take seconds -->
<p class="loading" role="status">Loading...</p>
{/if}


<style>

   .loading {
      /* fixed, unlike the note placed against '.page' below: there is no app box to place it in
         yet, or the box is hidden */
      position: fixed;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      margin: 0;
      padding: 0.6rem 1.2rem;
      border-radius: 0.5rem;
      background: #f8f6f4;
      color: #4e5052;
   }

   .page.loading-file {
      visibility: hidden;
   }

   .page {
      /* the note below is placed against this box and not against the window: the apps are built
         as self contained bundles to be embedded in a page, and a fixed position would put the
         note in the corner of the browser instead of the corner of the app */
      position: relative;

      width: 100%;
      height: 100%;
      margin: 0 auto;
      padding: 0;

      min-width: 1020px;
      min-height: 590px;
      max-width: 1900px;

      display: flex;
      flex-direction: column;
   }

   header{
      display: flex;
      justify-content: flex-start;
      flex-direction: row;
      background: #f8f6f4;
   }

   .header-group {
      width: min-content;
      min-width: 400px;
      margin: 0 0 0 0.25rem;
      display: grid;
      align-items: center;
      grid-template-columns: min-content 1fr;
      grid-template-rows: min-content min-content min-content min-content;
      grid-template-areas:
         "logo ."
         "logo title"
         "logo subtitle"
         "logo .";
   }

   .logo{
      width: 3.5rem;
      margin: 0.25rem;
      padding: 0.5rem;
      grid-area: logo;
   }

   .header-group > h1{
      margin: 0;
      padding: 0;
      text-align: left;
      font-size: 1.6rem;
      color: #4e5052;
      white-space: nowrap;
      grid-area: title;
   }

   .header-group > p{
      margin: 0;
      padding: 0;
      text-align: left;
      font-size: 1.1rem;
      color: #7e8082;
      grid-area: subtitle;
   }

   main{
      flex-basis: auto;
      flex-grow: 1;
      flex-shrink: 1;
      box-sizing: border-box;
      padding: 0.2rem 0;
   }

   main > div{
      height: 100%;
   }

   footer {
      display: grid;
      grid-template-columns: 20% 60% 20%;
      grid-template-areas: "session info .";
      align-items: center;
      justify-content: center;
      font-size: 0.9rem;
      flex-basis: min-content;
      flex-grow: 0;
      flex-shrink: 0;
      background: #4e5052;
      color: #c0c0c0;
      padding: 0.5em;
   }

   .session-error {
      position: absolute;
      left: 0.8rem;        /* above the session buttons, which are the left cell of the footer */
      bottom: 2.0rem;      /* clear of the footer, so those buttons stay reachable */
      z-index: 100;

      display: flex;
      align-items: center;
      gap: 0.6rem;
      max-width: 26rem;

      padding: 0.6rem 0.8rem;
      border-radius: 0.5rem;
      background: #c0392b;
      color: #fff;
      font-size: 0.9rem;
      line-height: 1.3;
      box-shadow: 0 2px 10px rgba(0, 0, 0, 0.35);
   }

   .session-error .session-error-close {
      flex-shrink: 0;
      display: flex;
      align-items: center;
      background: none;
      border: none;
      box-shadow: none;
      padding: 0;
      margin: 0;
      cursor: pointer;
   }

   .session-error .session-error-close svg {
      stroke: #fff;
      stroke-width: 12;
      stroke-linecap: round;
      opacity: 0.8;
   }

   .session-error .session-error-close:hover svg {
      opacity: 1;
   }

   footer .session {
      grid-area: session;
      padding-left: 0.5em;
      display: flex;
      align-items: center;
   }

   footer .session input {
      display: none;
   }

   footer .session .session-message {
      color: #c0c0c0;
      padding-right: 0.5em;
   }

   footer .session:hover .session-message {
      color: #fafafa
   }

   footer .session.no-buttons .clear-button,
   footer .session.no-buttons .upload-button,
   footer .session.no-buttons .download-button {
      display: none;
   }

   footer .session .clear-button,
   footer .session .upload-button,
   footer .session .download-button {
      font-size: 0.9rem;
      background: #5e6062;
      color: #c0c0c0;
      box-shadow: none;
      border: none;
      padding: 0em;
      margin: 0 0 0 0.2em;
      border-radius: 3px;
      height: 20px;
      box-sizing: border-box;
      display: inline-block;
   }

   footer .session .clear-button svg,
   footer .session .upload-button svg,
   footer .session .download-button svg{
      stroke-width: 0.75em;
      stroke-linecap: round;
      stroke-linejoin: round;
      stroke: #c0c0c0;
   }

   footer .session:hover .clear-button,
   footer .session:hover .upload-button,
   footer .session:hover .download-button{
      background: #35332d;
   }

   footer .session:hover .clear-button svg,
   footer .session:hover .upload-button svg,
   footer .session:hover .download-button svg{
      stroke: #fafafa;
   }

   footer .session .clear-button {
      margin-left: 0.5em;
   }

   footer .session .upload-button:hover,
   footer .session .download-button:hover {
      background-color: #6eb8ff;
   }

   footer .session .clear-button:hover {
      background-color: #dc143c;
   }


   footer .info {
      grid-area: info;
      display: flex;
      justify-content: center;
      align-items: center;
   }


   footer .info .button-info {
      cursor: default;
      border: none;
      box-shadow: none;
      background: transparent;
      font-weight: bold;
      color: #4e5052;
      border-radius: 0.2em;

      padding: 0.25em 0.5em;
      text-align: center;
      line-height: 1em;
      margin-left: 0.25em;
      margin-right: 0.5em;
      background-color: #f8ebc5;
   }

   footer .info .button-info:hover {
      background-color: #f7db89;
   }

   footer :global(p) {
      margin: 0 0.5em;
      text-align: center;
      padding: 0.15em;
   }

   footer :global(span),
   footer :global(a) {
      color: #f8ebc5
   }

   .hidden {
      display: none;
   }

   .nav,
   .nav * {
      user-select: none;
      -moz-user-select: none;
   }

   .nav {
      display: flex;
      flex-direction: row;
      justify-content: flex-end;
      list-style: none;
      margin: 0 100px 0 auto;
      padding: 0;
      align-self: flex-end;
   }

   .nav-item {
      list-style: none;
      margin: 0;
      border-bottom: 5px solid transparent;
   }

   .nav-item > button {
      cursor: pointer;
      padding: 0.5em 1em;
      border: none;
      box-shadow: none;
      background: transparent;
      font-size: inherit;
   }

   .nav-item:hover {
      border-bottom: 5px solid #80888f50;
   }

   .nav-item:hover > button {
      border-top-left-radius: 5px;
      border-top-right-radius: 5px;
      background: #00000010;
   }

   .nav-item.selected {
      border-bottom: 5px solid #80888f;
   }

   .nav-item.selected > button{
      font-weight: bold;
      cursor: default;
   }

   .nav-item.selected:hover {
      border-bottom: 5px solid #80888f;
   }


   @media (min-width: 1600px) {
      .page > header {
         border-bottom-right-radius: 0.25rem;
         border-bottom-left-radius: 0.25rem;
      }

      .page > footer {
         border-top-right-radius: 0.25rem;
         border-top-left-radius: 0.25rem;
      }
   }


   .info-container {
      width: 900px;
   }

   .info-container > :global(p) {
      font-size: 1.15em;
      line-height: 1.35em;
      padding: 0 1em 0.5em 1em;
      margin: 0;
   }

   .info-container > :global(.release-info) {
      font-size: 1em;
      color: #808080;
      line-height: 1.25em;
      padding: 0 1.2em 0.5em 1.2em;
      margin: 0;
   }

   .info-container > :global(h2) {
      font-size: 1.5em;
      line-height: 1.25em;
      padding: 0em 1rem 0.5em 1rem;
      margin: 0;
   }

   .info-container > :global(h3) {
      font-size: 1.15em;
      line-height: 1.25em;
      padding: 1rem 1em 0.25em 1em;
      margin: 0;
   }

   .info-container > :global(ul) {
      margin: 0;
   }

   .info-container > :global(ul > li) {
      font-size: 1.15em;
      line-height: 1.25em;
      padding: 0 0.5em 0.5em 0em;
      margin: 0;
   }

   .info-container :global(a) {
      color: #ae8032;
   }

   .info-container :global(a:hover) {
      color: #eb9707;
   }

   .info-container:last-child {
      padding-bottom: 1rem;
   }


</style>